import {
  forwardRef,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';

import { Icon, type IconName } from '@/components/ui';
// Submodule paths, not barrels: the catalogue's barrel reaches back into the shell.
import { CategoryIcon } from '@/features/catalog/CategoryIcon';
import { categoryHref, productHref } from '@/features/catalog/categorySlug';
import { categoryStyle, categoryVars } from '@/features/catalog/categoryStyle';
import {
  EMPTY_CATALOG_FILTERS,
  useCatalogCategoriesQuery,
  useCatalogItemsQuery,
  useCatalogPricesQuery,
  useCatalogUnitsQuery,
} from '@/features/catalog/queries';
import { SEARCH_DEBOUNCE_MS, toSearchTerm } from '@/features/catalog/searchRules';
import { useShoppingBuying } from '@/features/catalog/useShoppingBuying';
import { formatClosingTime } from '@/features/orders/closingWindow';
import { formatPrice, toAmount } from '@/features/pricing/money';
import { todayInSaoPaulo } from '@/features/pricing/referenceDate';
import { useDebouncedValue } from '@/lib/hooks';
import { formatQuantity } from '@/lib/format';

import type { StoreSummary } from './useStoreSummary';
import styles from './StoreSearch.module.css';

const RECENT_KEY = 'instoque.buscas-recentes';
const RECENT_MAX = 3;
const PRODUCT_MAX = 4;

/** Recent searches: a per-browser convenience, so storage that throws simply means none. */
function readRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const parsed: unknown = raw === null ? [] : JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function rememberSearch(term: string): void {
  try {
    const next = [term, ...readRecent().filter((x) => x !== term)].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // Blocked storage: nothing to remember, nothing broken.
  }
}

const normalise = (value: string) => value.normalize('NFD').replace(/[̀-ͯ]/gu, '').toLowerCase();

/** "arr" in "Arroz agulhinha" → <strong>Arr</strong>oz agulhinha (Figma D2 bolds the match). */
function highlight(text: string, query: string): ReactNode {
  const at = normalise(text).indexOf(normalise(query));
  if (query === '' || at < 0) {
    return text;
  }
  return (
    <>
      {text.slice(0, at)}
      <strong>{text.slice(at, at + query.length)}</strong>
      {text.slice(at + query.length)}
    </>
  );
}

export interface StoreSearchProps {
  readonly summary: StoreSummary;
  /** `bar`: the header's central field. `row`: the phone's own row under the header line. */
  readonly variant?: 'bar' | 'row' | undefined;
}

interface Option {
  readonly id: string;
  readonly run: () => void;
}

/**
 * The storefront search (Figma D2, M2): always in sight, ⌘K focuses it from any screen. Typing
 * three letters lists the products that match — price, and either "No pedido · 10 kg" or
 * "Adicionar" — then the matching categories, the recent searches and "Ir para" (the destinations
 * the ⌘K palette used to hold). Enter on the text opens the whole result in the quick list.
 *
 * Motion (D2): the veil darkens 0 → 42 % (200 ms) and the panel rises 0,96 → 1 from −6 px
 * (180 ms, out-strong); sections cascade 30 ms apart, products 40 ms. Closing is the reverse,
 * without the cascade (120 ms).
 */
export const StoreSearch = forwardRef<HTMLInputElement, StoreSearchProps>(function StoreSearch(
  { summary, variant = 'bar' },
  forwardedRef,
) {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [active, setActive] = useState(-1);
  const [recent, setRecent] = useState<string[]>(readRecent);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listId = useId();
  const [referenceDate] = useState(() => todayInSaoPaulo());

  const term = toSearchTerm(useDebouncedValue(value, SEARCH_DEBOUNCE_MS));
  const shownTerm = value.trim();
  const searching = term !== '' && open;
  const items = useCatalogItemsQuery({ ...EMPTY_CATALOG_FILTERS, search: term }, searching);
  const products = useMemo(
    () =>
      searching
        ? (items.data?.pages ?? []).flatMap((page) => page.items).slice(0, PRODUCT_MAX)
        : [],
    [items.data, searching],
  );
  const prices = useCatalogPricesQuery(
    products.map((item) => item.sku.id),
    referenceDate,
  );
  const units = useCatalogUnitsQuery();
  const { buying } = useShoppingBuying(products);
  const categories = useCatalogCategoriesQuery();
  const matchingCategories = useMemo(
    () =>
      shownTerm.length < 2
        ? []
        : (categories.data ?? []).filter((name) => normalise(name).includes(normalise(shownTerm))),
    [categories.data, shownTerm],
  );

  const close = () => {
    if (!open) return;
    setClosing(true);
    window.setTimeout(() => {
      setOpen(false);
      setClosing(false);
      setActive(-1);
    }, 120);
  };

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) {
        close();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
    };
  });

  const go = (to: string) => {
    close();
    inputRef.current?.blur();
    void navigate(to, { viewTransition: true });
  };

  const searchAll = (text: string) => {
    const trimmed = text.trim();
    if (trimmed === '') return;
    rememberSearch(trimmed);
    setRecent(readRecent());
    go(`/catalogo?busca=${encodeURIComponent(trimmed)}`);
  };

  const goTo: { id: string; icon: IconName; label: string; note?: string; to: string }[] = [
    {
      id: 'pedidos',
      icon: 'receipt',
      label: 'Pedidos',
      to: '/pedidos',
      ...(summary.openOrder !== undefined
        ? {
            note: `${String(summary.openOrderCount)} ${summary.openOrderCount === 1 ? 'aberto' : 'abertos'} · fecha às ${formatClosingTime(summary.openOrder.closes_at)}`,
          }
        : {}),
    },
    ...(summary.paysBoletos
      ? [
          {
            id: 'boletos',
            icon: 'barcode' as const,
            label: 'Boletos',
            to: '/pagamentos',
            ...(summary.boletosDue.length > 0
              ? { note: `${String(summary.boletosDue.length)} a vencer` }
              : {}),
          },
        ]
      : []),
    { id: 'historico', icon: 'chart', label: 'Histórico de compras', to: '/pedidos/historico' },
  ];

  const options: Option[] = [
    ...products.map((item) => ({
      id: `p-${item.sku.id}`,
      run: () => go(productHref(item.sku.id)),
    })),
    ...matchingCategories.map((name) => ({ id: `c-${name}`, run: () => go(categoryHref(name)) })),
    ...(shownTerm === '' ? recent : []).map((text) => ({
      id: `r-${text}`,
      run: () => {
        setValue(text);
        searchAll(text);
      },
    })),
    ...goTo.map((entry) => ({ id: `g-${entry.id}`, run: () => go(entry.to) })),
  ];
  const indexOf = (id: string) => options.findIndex((option) => option.id === id);
  const activeId = options[active]?.id;

  const setRefs = (node: HTMLInputElement | null) => {
    inputRef.current = node;
    if (typeof forwardedRef === 'function') forwardedRef(node);
    else if (forwardedRef !== null) forwardedRef.current = node;
  };

  let cascade = 0;
  const section = (title: string, body: ReactNode) => (
    <section className={styles.section} style={{ '--i': cascade++ } as CSSProperties}>
      <p className={styles.sectionTitle}>{title}</p>
      {body}
    </section>
  );

  return (
    <div
      ref={rootRef}
      className={styles.root}
      data-variant={variant}
      data-open={open ? '' : undefined}
    >
      <div className={styles.field}>
        <Icon name="search" size={20} strokeWidth={1.5} className={styles.fieldIcon} />
        <input
          ref={setRefs}
          type="search"
          className={styles.input}
          placeholder={
            variant === 'row'
              ? 'Buscar produtos ou códigos'
              : 'Buscar produtos, marcas ou códigos (ex.: arroz, ARR-5KG)'
          }
          aria-label="Buscar produtos, marcas ou códigos"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeId === undefined ? undefined : `${listId}-${activeId}`}
          autoComplete="off"
          enterKeyHint="search"
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            setActive(-1);
            setOpen(true);
          }}
          onFocus={() => {
            setRecent(readRecent());
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              setOpen(true);
              const step = event.key === 'ArrowDown' ? 1 : -1;
              const count = options.length;
              setActive((current) =>
                count === 0
                  ? -1
                  : current < 0
                    ? step === 1
                      ? 0
                      : count - 1
                    : (current + step + count) % count,
              );
            } else if (event.key === 'Enter') {
              event.preventDefault();
              const option = options[active];
              if (option !== undefined) option.run();
              else searchAll(value);
            } else if (event.key === 'Escape') {
              close();
              inputRef.current?.blur();
            }
          }}
        />
        {variant === 'bar' ? (
          <kbd className={styles.shortcut} aria-hidden="true">
            ⌘K
          </kbd>
        ) : null}
      </div>

      {open
        ? createPortal(
            <div
              className={styles.veil}
              data-closing={closing ? '' : undefined}
              aria-hidden="true"
            />,
            document.body,
          )
        : null}

      {open ? (
        <div
          className={styles.panel}
          data-closing={closing ? '' : undefined}
          id={listId}
          role="listbox"
          aria-label="Sugestões da busca"
        >
          {products.length > 0
            ? section(
                'Produtos',
                <ul className={styles.list}>
                  {products.map((item, index) => {
                    const { sku } = item;
                    const id = `p-${sku.id}`;
                    const price = prices.data?.lines.get(sku.id);
                    const amount = toAmount(price?.unit_price);
                    const unit = units.data?.get(sku.base_unit_id);
                    const inOrder = buying?.quantities.get(sku.id) ?? 0;
                    const style = categoryStyle(sku.category);
                    return (
                      <li
                        key={sku.id}
                        id={`${listId}-${id}`}
                        role="option"
                        aria-selected={activeId === id}
                        className={styles.product}
                        style={{ '--j': index } as CSSProperties}
                        data-active={activeId === id ? '' : undefined}
                        onMouseEnter={() => {
                          setActive(indexOf(id));
                        }}
                      >
                        <Link
                          to={productHref(sku.id)}
                          className={styles.productLink}
                          tabIndex={-1}
                          onClick={(event) => {
                            event.preventDefault();
                            go(productHref(sku.id));
                          }}
                        >
                          <span className={styles.thumb} style={categoryVars(style)}>
                            <CategoryIcon style={style} size={24} />
                          </span>
                          <span className={styles.productText}>
                            <span className={styles.productName}>
                              {highlight(sku.description, shownTerm)}
                            </span>
                            <span className={styles.productMeta}>
                              {[
                                sku.brand,
                                sku.code,
                                [sku.category, sku.subcategory].filter(Boolean).join(' › '),
                              ]
                                .filter((part) => part != null && part !== '')
                                .join(' · ')}
                            </span>
                          </span>
                        </Link>
                        {amount !== null ? (
                          <span className={styles.productPrice}>
                            {formatPrice(amount, price?.currency)}
                            {unit !== undefined ? ` / ${unit.symbol}` : ''}
                          </span>
                        ) : null}
                        {buying !== undefined && inOrder > 0 ? (
                          <span className={styles.inOrder}>
                            No pedido · {formatQuantity(inOrder)}
                            {unit !== undefined ? ` ${unit.symbol}` : ''}
                          </span>
                        ) : buying !== undefined && amount !== null && item.status === 'active' ? (
                          <button
                            type="button"
                            className={styles.add}
                            tabIndex={-1}
                            onClick={() => {
                              const usual = buying.usual.get(sku.id)?.quantity;
                              const minimum = toAmount(sku.minimum_order_quantity) ?? 1;
                              buying.onCommit(sku.id, usual ?? Math.max(minimum, 1));
                            }}
                          >
                            <Icon name="plus" size={14} strokeWidth={2.2} />
                            Adicionar
                          </button>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>,
              )
            : null}

          {matchingCategories.length > 0
            ? section(
                'Categorias',
                <ul className={styles.list}>
                  {matchingCategories.map((name) => {
                    const id = `c-${name}`;
                    return (
                      <li
                        key={name}
                        id={`${listId}-${id}`}
                        role="option"
                        aria-selected={activeId === id}
                        className={styles.row}
                        data-active={activeId === id ? '' : undefined}
                        onMouseEnter={() => {
                          setActive(indexOf(id));
                        }}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          go(categoryHref(name));
                        }}
                      >
                        <Icon name="grid" size={16} className={styles.rowIconBrand} />
                        <span className={styles.rowLabel}>{highlight(name, shownTerm)}</span>
                      </li>
                    );
                  })}
                </ul>,
              )
            : null}

          {shownTerm === '' && recent.length > 0
            ? section(
                'Buscas recentes',
                <ul className={styles.chips}>
                  {recent.map((text) => {
                    const id = `r-${text}`;
                    return (
                      <li
                        key={text}
                        id={`${listId}-${id}`}
                        role="option"
                        aria-selected={activeId === id}
                        className={styles.chip}
                        data-active={activeId === id ? '' : undefined}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          setValue(text);
                          searchAll(text);
                        }}
                      >
                        <Icon name="clock" size={14} strokeWidth={1.6} />
                        {text}
                      </li>
                    );
                  })}
                </ul>,
              )
            : null}

          {section(
            'Ir para',
            <ul className={styles.list}>
              {goTo.map((entry) => {
                const id = `g-${entry.id}`;
                return (
                  <li
                    key={entry.id}
                    id={`${listId}-${id}`}
                    role="option"
                    aria-selected={activeId === id}
                    className={styles.row}
                    data-active={activeId === id ? '' : undefined}
                    onMouseEnter={() => {
                      setActive(indexOf(id));
                    }}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      go(entry.to);
                    }}
                  >
                    <Icon name={entry.icon} size={16} className={styles.rowIcon} />
                    <span className={styles.rowLabel}>{entry.label}</span>
                    {entry.note !== undefined ? (
                      <span className={styles.rowNote}>{entry.note}</span>
                    ) : null}
                  </li>
                );
              })}
            </ul>,
          )}

          {shownTerm !== '' ? (
            <div className={styles.foot}>
              <button
                type="button"
                className={styles.all}
                onClick={() => {
                  searchAll(value);
                }}
              >
                Ver todos os resultados para “{shownTerm}”
                <Icon name="chevronRight" size={14} />
              </button>
              <p className={styles.hints} aria-hidden="true">
                <span>↑↓ navegar</span>
                <span>↵ abrir</span>
                <span>Esc fechar</span>
                <span>⌘K em qualquer tela</span>
              </p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
});
